/**
 * useRemoteCursors Hook - 整合 Presence 與 CodeMirror 遠端游標
 *
 * 將 RoomParticipant 資料轉換為 RemoteCursorData 並更新 EditorView
 *
 * @module @linkode/web/editor/cm/useRemoteCursors
 * @see SPEC-007 Requirements 6.2
 */

import { useEffect, useCallback, useRef } from 'react';
import type { EditorView } from '@codemirror/view';
import type { RoomParticipant, Position } from '@linkode/shared';
import { updateRemoteCursors, offsetToPosition, type RemoteCursorData } from './remoteCursor';

// ============================================================================
// Types
// ============================================================================

export interface UseRemoteCursorsOptions {
  /** EditorView instance (can be null during initialization) */
  view: EditorView | null;
  /** Remote participants from usePresence hook */
  participants: RoomParticipant[];
  /** Debounce interval for cursor updates (ms) */
  debounceMs?: number;
}

export interface UseRemoteCursorsResult {
  /** Convert current editor selection to Position for presence update */
  getLocalCursorPosition: () => Position | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to synchronize remote participant cursors with CodeMirror editor
 *
 * @param options - Hook options
 * @returns Helper functions for local cursor position
 *
 * @example
 * ```tsx
 * const { getLocalCursorPosition } = useRemoteCursors({
 *   view: editorView,
 *   participants,
 * });
 *
 * // When local cursor moves, update presence
 * const handleCursorChange = () => {
 *   const position = getLocalCursorPosition();
 *   updateCursor(position);
 * };
 * ```
 */
export function useRemoteCursors({
  view,
  participants,
  debounceMs = 50,
}: UseRemoteCursorsOptions): UseRemoteCursorsResult {
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update remote cursors when participants change
  useEffect(() => {
    if (!view) return;

    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce the update to avoid excessive re-renders
    updateTimeoutRef.current = setTimeout(() => {
      const cursorData: RemoteCursorData[] = participants
        .filter((p) => p.presence.cursor !== null)
        .map((p) => ({
          userId: p.userId,
          cursor: p.presence.cursor,
          displayName: p.presence.displayName,
          colorId: p.presence.colorId,
        }));

      updateRemoteCursors(view, cursorData);
    }, debounceMs);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [view, participants, debounceMs]);

  // Clear cursors when view is destroyed
  useEffect(() => {
    return () => {
      if (view) {
        try {
          updateRemoteCursors(view, []);
        } catch {
          // View may be destroyed, ignore
        }
      }
    };
  }, [view]);

  // Get local cursor position from editor
  const getLocalCursorPosition = useCallback((): Position | null => {
    if (!view) return null;

    const { head } = view.state.selection.main;
    return offsetToPosition(view, head);
  }, [view]);

  return {
    getLocalCursorPosition,
  };
}

export default useRemoteCursors;
